import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Clock3, IndianRupee, ListChecks, Mail, MessageCircle, Zap } from "lucide-react";
import { API, Shell, track, useT } from "./Shell";
import { GoogleButton } from "../portal/PortalAuth";
import "./testTheme.css";

const FEATURE_ICONS = [ListChecks, Clock3, Zap, MessageCircle, IndianRupee];

const BLANK = { name: "", whatsapp: "", current_class: "", target_exam: "", email: "", school: "", consent: false, promo_consent: false };

export default function TestLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const T = useT();
  const [cfg, setCfg] = useState(null);
  const [board, setBoard] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [error, setError] = useState(location.state?.quickError || "");
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState({ email: "", code: "", sent: false });
  const [notice, setNotice] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [quick, setQuick] = useState(location.state?.quickComplete || null);
  const [qform, setQform] = useState({ whatsapp: "", current_class: "", target_exam: "", consent: false });
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    track("page_visit");
    axios.get(`${API}/test/config`).then(({ data }) => setCfg(data)).catch(() => {});
    axios.get(`${API}/test/leaderboard`).then(({ data }) => setBoard(data.top)).catch(() => {});
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const goToTopics = (studentId, name) => {
    localStorage.setItem("pjp-student", studentId);
    localStorage.setItem("pjp-student-name", name || "");
    navigate("/physics-test/topics");
  };

  const handleQuickResult = (data) => {
    if (data.needs_details) {
      setQuick(data);
      setError("");
    } else {
      goToTopics(data.student_id, data.name);
    }
  };

  const sendOtp = async (e) => {
    e?.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);
    try {
      await axios.post(`${API}/test/auth/otp/request`, { email: otp.email });
      setOtp((c) => ({ ...c, sent: true }));
      setResendIn(60);
      setNotice(T.qs_sent);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : T.l_err_generic);
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/test/auth/otp/verify`, { email: otp.email, code: otp.code });
      handleQuickResult(data);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : T.l_err_generic);
    } finally { setLoading(false); }
  };

  const submitQuickComplete = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[6-9]\d{9}$/.test(qform.whatsapp)) { setError(T.l_err_phone); return; }
    if (!qform.consent) { setError(T.l_err_consent); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/test/auth/complete`, { student_id: quick.student_id, ...qform });
      goToTopics(data.student_id, data.name);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : T.l_err_generic);
    } finally { setLoading(false); }
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!/^[6-9]\d{9}$/.test(form.whatsapp)) { setError(T.l_err_phone); return; }
    if (!form.consent) { setError(T.l_err_consent); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/test/register`, form);
      goToTopics(data.student_id, data.name);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : T.l_err_generic);
    } finally { setLoading(false); }
  };

  return (
    <Shell>
      <div className="tp-wrap">
        <section className="tp-hero">
          <div className="tp-eyebrow">{T.l_eyebrow}</div>
          <h1 data-testid="tp-headline">{T.l_h1}</h1>
          <p className="tp-sub">{T.l_sub}</p>
          <p>{T.l_desc}</p>
          <div className="tp-cta-row">
            <a className="tp-btn" href="#register" data-testid="tp-start-cta">{T.l_cta} <ArrowRight size={17} /></a>
          </div>
          <p className="tp-note">{T.l_note}</p>
          {cfg && !cfg.published && <p className="tp-note" style={{ marginTop: 12, color: "#b54708" }} data-testid="tp-not-live">{T.l_notlive}</p>}
        </section>
        <section className="tp-cards" data-testid="tp-features">
          {T.l_features.map(([title, copy], i) => { const Icon = FEATURE_ICONS[i]; return <div className="tp-card" key={title}><div className="tp-ico"><Icon size={20} /></div><h3>{title}</h3><p>{copy}</p></div>; })}
        </section>
        {board && <section className="tp-section"><div className="tp-panel" style={{ maxWidth: 640, margin: "auto" }} data-testid="tp-leaderboard">
          <div className="tp-eyebrow">{T.lb_title}</div>
          <p className="tp-muted" style={{ marginBottom: 18 }}>{T.lb_sub}</p>
          {board.length === 0 ? <p className="tp-note" data-testid="tp-lb-empty">{T.lb_empty}</p> : board.map((row) => (
            <div key={row.rank} className="tp-lb-row" data-testid={`tp-lb-row-${row.rank}`}>
              <span className={`tp-lb-rank rank-${row.rank}`}>{row.rank}</span>
              <span className="tp-lb-name">{row.name}<small>{row.topic_name}</small></span>
              <span className="tp-lb-score">{row.score}/{row.max_score}<small>{row.correct}/5 {T.r_correct.toLowerCase()}</small></span>
            </div>
          ))}
        </div></section>}
        {cfg && <section className="tp-section"><div className="tp-panel tp-instructor" data-testid="tp-instructor">
          <img src={cfg.instructor.photo_url || "/mentor.jpg"} alt={cfg.instructor.name} />
          <div><div className="tp-eyebrow">{T.l_mentor}</div><h3>{cfg.instructor.name}</h3><div className="tp-role">{cfg.instructor.designation}</div><p className="tp-muted">{cfg.instructor.bio}</p></div>
        </div></section>}
        <section className="tp-section" id="register"><div className="tp-panel" style={{ maxWidth: 640, margin: "auto" }}>
          <div className="tp-eyebrow">{T.l_step}</div>
          <h2 className="tp-h2">{T.l_regtitle}</h2>
          <p className="tp-muted" style={{ marginBottom: 22 }}>{T.l_regsub}</p>
          {error && <p className="tp-error" role="alert" data-testid="tp-register-error">{error}</p>}
          {quick ? (
            <form onSubmit={submitQuickComplete} data-testid="tp-quick-complete-form">
              <h3 style={{ marginTop: 0 }} data-testid="tp-quick-title">{T.qs_details_title}</h3>
              <p className="tp-muted" style={{ marginBottom: 16 }}>{T.qs_details_sub}</p>
              <div className="tp-grid-2">
                <label className="tp-label">{T.l_wa}<input required value={qform.whatsapp} onChange={(e) => setQform((c) => ({ ...c, whatsapp: e.target.value }))} placeholder={T.l_ph_phone} inputMode="numeric" data-testid="tp-quick-whatsapp" /></label>
                <label className="tp-label">{T.l_class}<select required value={qform.current_class} onChange={(e) => setQform((c) => ({ ...c, current_class: e.target.value }))} data-testid="tp-quick-class"><option value="">{T.l_sel_class}</option>{T.l_classes.map((c) => <option key={c}>{c}</option>)}</select></label>
              </div>
              <label className="tp-label">{T.l_exam}<select required value={qform.target_exam} onChange={(e) => setQform((c) => ({ ...c, target_exam: e.target.value }))} data-testid="tp-quick-exam"><option value="">{T.l_sel_exam}</option>{T.l_exams.map((x) => <option key={x}>{x}</option>)}</select></label>
              <label className="tp-check"><input type="checkbox" checked={qform.consent} onChange={(e) => setQform((c) => ({ ...c, consent: e.target.checked }))} data-testid="tp-quick-consent" /> {T.l_consent_a} <a href="/privacy" target="_blank" rel="noreferrer">{T.privacy}</a> {T.l_consent_b}</label>
              <button className="tp-btn" style={{ width: "100%", marginTop: 8 }} disabled={loading} type="submit" data-testid="tp-quick-submit">{loading ? T.l_submitting : T.qs_start} <ArrowRight size={16} /></button>
            </form>
          ) : (
            <div style={{ marginBottom: 22 }} data-testid="tp-quickstart">
              <h3 style={{ marginTop: 0 }}>{T.qs_title}</h3>
              <p className="tp-muted" style={{ marginBottom: 14 }}>{T.qs_sub}</p>
              {notice && <p className="tp-pill ok" style={{ marginBottom: 12 }} data-testid="tp-otp-notice">{notice}</p>}
              <GoogleButton redirectPath="/physics-test" label={T.qs_google} testid="tp-google-btn" />
              <p className="tp-note" style={{ textAlign: "center", margin: "12px 0" }}>— {T.qs_or} —</p>
              <form onSubmit={otp.sent ? verifyOtp : sendOtp} data-testid="tp-otp-form">
                <label className="tp-label">{T.qs_email_label}<input type="email" required value={otp.email} disabled={otp.sent} onChange={(e) => setOtp((c) => ({ ...c, email: e.target.value }))} placeholder="you@example.com" data-testid="tp-otp-email" /></label>
                {otp.sent && (
                  <label className="tp-label">{T.qs_code}<input required value={otp.code} onChange={(e) => setOtp((c) => ({ ...c, code: e.target.value.replace(/\D/g, "").slice(0, 6) }))} placeholder="••••••" inputMode="numeric" autoFocus data-testid="tp-otp-code" /></label>
                )}
                <button className="tp-btn" style={{ width: "100%", marginTop: 4 }} disabled={loading} type="submit" data-testid={otp.sent ? "tp-otp-verify" : "tp-otp-send"}>
                  {loading ? T.l_submitting : otp.sent ? <>{T.qs_verify} <ArrowRight size={16} /></> : <>{T.qs_send} <Mail size={16} /></>}
                </button>
                {otp.sent && (
                  <p className="tp-note" style={{ marginTop: 10, textAlign: "center" }}>
                    {resendIn > 0 ? <span data-testid="tp-otp-timer">{T.qs_resend_wait(resendIn)}</span> : (
                      <button type="button" className="tp-back" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => setOtp((c) => ({ ...c, sent: false, code: "" }))} data-testid="tp-otp-resend">{T.qs_resend}</button>
                    )}
                  </p>
                )}
              </form>
              <p className="tp-note" style={{ textAlign: "center", margin: "14px 0 0" }}>— {T.qs_or_form} —</p>
            </div>
          )}
          {!quick && (
          <form onSubmit={submit} data-testid="tp-register-form">
            <div className="tp-grid-2">
              <label className="tp-label">{T.l_name}<input required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder={T.l_ph_name} data-testid="tp-reg-name" /></label>
              <label className="tp-label">{T.l_wa}<input required value={form.whatsapp} onChange={(e) => update("whatsapp", e.target.value)} placeholder={T.l_ph_phone} inputMode="numeric" data-testid="tp-reg-whatsapp" /></label>
            </div>
            <div className="tp-grid-2">
              <label className="tp-label">{T.l_class}<select required value={form.current_class} onChange={(e) => update("current_class", e.target.value)} data-testid="tp-reg-class"><option value="">{T.l_sel_class}</option>{T.l_classes.map((c) => <option key={c}>{c}</option>)}</select></label>
              <label className="tp-label">{T.l_exam}<select required value={form.target_exam} onChange={(e) => update("target_exam", e.target.value)} data-testid="tp-reg-exam"><option value="">{T.l_sel_exam}</option>{T.l_exams.map((x) => <option key={x}>{x}</option>)}</select></label>
            </div>
            <div className="tp-grid-2">
              <label className="tp-label">{T.l_email}<input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" data-testid="tp-reg-email" /></label>
              <label className="tp-label">{T.l_school}<input value={form.school} onChange={(e) => update("school", e.target.value)} placeholder={T.l_school.split(" (")[0]} data-testid="tp-reg-school" /></label>
            </div>
            <label className="tp-check"><input type="checkbox" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} data-testid="tp-reg-consent" /> {T.l_consent_a} <a href="/privacy" target="_blank" rel="noreferrer">{T.privacy}</a> {T.l_consent_b}</label>
            <label className="tp-check"><input type="checkbox" checked={form.promo_consent} onChange={(e) => update("promo_consent", e.target.checked)} data-testid="tp-reg-promo" /> {T.l_promo}</label>
            <button className="tp-btn" style={{ width: "100%", marginTop: 8 }} disabled={loading} type="submit" data-testid="tp-register-submit">{loading ? T.l_submitting : T.l_submit} <ArrowRight size={16} /></button>
          </form>
          )}
        </div></section>
      </div>
    </Shell>
  );
}
